import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { Project, TextureCategory } from '../modules/openapi';
import { Element } from '../modules/model-viewer-urbasee/entities/element.entity';
import { ElementVariant } from '../modules/model-viewer-urbasee/entities/element-variant.entity';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from '@angular/router';
import { Keyword } from '../modules/model-viewer-urbasee/entities/keyword.entity';
import { DOCUMENT } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QrcodeModalComponent } from './qrcode-modal/qrcode-modal.component';
import { TranslocoService } from '@jsverse/transloco';
import { tap, timer } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private queryParams: any;
  // Variables
  //public projectId?: number = 24653; // Autoporté 3x3
  public projectId?: number = 24664; // Autoporté 3x4
  //public projectId?: number = 24626; // Test 1 accessoire

  public accessoryElement?: Element; // accessory element to display
  public accessoryElementVariant?: ElementVariant; // Variant to apply on accessory element
  public mainElementVariant?: ElementVariant; // Variant to apply on main element

  public token: string = 'urbasee-954bc640f3b4aa80774051e2fdb79f3c599';
  public mainElementName: string = 'PrimaryElement';
  public canActivateAr = false;
  public forceAr = false;

  // Datas loaded
  public elements: Element[] = [];
  public project?: Project;
  public textureCategories?: TextureCategory[] = [];
  public keywords?: Keyword[] = [];

  // Filtered datas
  public mainElement?: Element;
  public accessories: Element[] = []; // accessories withs colors (filtered only when project loaded)
  public projectVariants: ElementVariant[] = [];

  public menuMainElementVariantOpened: boolean = false;
  public menuAccessoryElementOpened: boolean = false;
  public menuAccessoryElementVariantOpened: boolean = false;

  public canActivateFullscreen = true;
  public canCloseIframe = true;

  public documentElement: any;
  public isFullscreen = false;

  public locale: string;
  public isReady = false;

  constructor(
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private translocoService: TranslocoService,
    @Inject(DOCUMENT) private document: any
  ) {
    this.locale = translocoService.getActiveLang();
    translocoService.setActiveLang(this.locale);
    this.canCloseIframe = window.self !== window.top;
  }

  ngOnInit(): void {
    //TODO: set canActivateFullscreen if on iframe

    this.documentElement = document.documentElement;

    this.route.queryParams.subscribe((queryParams) => {
      /*
      [token]="'MONTOKEN'"
      [project-id]="PROJECTID"
      [main-color]="ID"
      [accessory-name]="'ACCESSORYNAME'"
      [accessory-color]="ID"
      [lang]="'fr'"
      [force-ar]="true"
      */
      this.queryParams = queryParams;
      if (this.queryParams['project-id']) {
        this.projectId = parseInt(queryParams['project-id']);
      }
      if (this.queryParams['token']) {
        this.token = queryParams['token'];
      }
      if (this.queryParams['lang']) {
        this.locale = queryParams['lang'];
        this.translocoService.setActiveLang(this.locale);
      }
      if (this.queryParams['force-ar']) {
        this.forceAr = queryParams['force-ar'] == 'true';
      }
    });
  }

  elementsLoaded(elements: Element[]) {
    this.elements = elements;
    this.populateElements();
  }
  projectLoaded(project: Project) {
    this.project = project;
    this.populateElements();
  }
  modelViewerReady(e: any) {
    this.populateElements();
    this.isReady = true;
  }
  textureCategoriesLoaded(textureCategories: TextureCategory[]) {
    this.textureCategories = textureCategories;
  }
  keywordsLoaded(keywords: Keyword[]) {
    this.keywords = keywords || [];
  }

  toggleMenuMainElementVariant() {
    this.menuAccessoryElementVariantOpened = false;
    this.menuAccessoryElementOpened = false;
    this.menuMainElementVariantOpened = !this.menuMainElementVariantOpened;
  }

  toggleMenuAccessoryElement() {
    this.menuMainElementVariantOpened = false;
    this.menuAccessoryElementVariantOpened = false;
    this.menuAccessoryElementOpened = !this.menuAccessoryElementOpened;
  }

  toggleMenuAccessoryElementVariant() {
    this.menuMainElementVariantOpened = false;
    this.menuAccessoryElementOpened = false;
    this.menuAccessoryElementVariantOpened =
      !this.menuAccessoryElementVariantOpened;
  }

  private populateElements() {
    if (this.project?.id && this.elements.length > 0) {
      // Fetch variants for main element structure
      this.mainElement = this.elements
        // Search for element Structure
        ?.find((element) => element.name === this.mainElementName);
      this.projectVariants =
        // Extract only ElementVariantTexture
        this.elements
          // Search for element Structure
          ?.find((element) => element.name === this.mainElementName)
          ?.variants || [];

      // Filter colors (keep only colors that are in the project texture colors)
      this.accessories = this.elements?.filter(
        (element) => element.name !== this.mainElementName
      );

      // Set the main color (if specified in query params, else set the first variant that apply on load)
      if (this.queryParams['main-color']) {
        this.mainElementVariant = this.projectVariants?.find(
          (variant) => variant.name === this.queryParams['main-color']
        );
      } else {
        this.mainElementVariant = this.mainElement?.variants?.find(
          (variant) => variant.applyOnLoad
        );
      }

      // Set the accessory element (if specified in query params)
      if (this.queryParams['accessory']) {
        this.accessoryElement = this.elements.find(
          (element) => element.name === this.queryParams['accessory']
        );
      }

      // Set the accessory color (if specified in query params, else set the first variant that apply on load)
      if (this.queryParams['accessory-color']) {
        this.accessoryElementVariant = this.accessoryElement?.variants?.find(
          (variant) => variant.name === this.queryParams['accessory-color']
        );
      }
    }
  }

  setMainElementVariant(mainElementVariant: ElementVariant) {
    this.mainElementVariant = mainElementVariant;
  }

  enableAccessoryElement(element: Element) {
    if (this.accessoryElement === element) {
      this.accessoryElement = undefined;
    } else {
      this.accessoryElement = element;
    }
    // Attempt to find the variant with same name that previous variant
    let accessoryElementVariant = element.variants?.find(
      (variant) => this.accessoryElementVariant?.name === variant.name
    );
    // If not found, search for a variant that apply on load
    if (!accessoryElementVariant) {
      accessoryElementVariant = element.variants?.find(
        (variant) => variant.applyOnLoad
      );
    }
    this.accessoryElementVariant = accessoryElementVariant;
  }

  setAccessoryElementVariant(accessoryElementVariant: ElementVariant) {
    this.accessoryElementVariant = accessoryElementVariant;
  }

  onCanActivateAr(state: boolean) {
    this.canActivateAr = state;
    this.forceAr = false;
  }

  // Attempt to activate AR. If not possible, display a QR code to scan
  // If possible, force AR to true, and auto switch false after 2s
  activateAr() {
    this.forceAr = true;
    if (this.canActivateAr) {
      this.forceAr = true;
      console.log('Activate AR');
    } else {
      console.warn('Cant activate AR');

      const modalRef = this.modalService.open(QrcodeModalComponent, {
        size: 'l',
      });
      (<QrcodeModalComponent>modalRef.componentInstance).data =
        window.location.href; // + '&force-ar=true'; // Force AR when opening the QR code (FIXME: dont 100% work on mobiles devices - too slow?)
      (<QrcodeModalComponent>modalRef.componentInstance).type = 'url';
    }

    // auto switch off force ar after 2s
    timer(2000)
      .pipe(tap(() => {}))
      .subscribe(() => {
        this.forceAr = false;
      });
  }

  toogleFullscreen() {
    if (this.isFullscreen) {
      this._closeFullscreenFunction().then(() => {
        this.isFullscreen = false;
      });
    } else {
      this._requestFullscreenFunction().then(() => {
        this.isFullscreen = true;
      });
    }
  }

  _requestFullscreenFunction() {
    //this.isFullscreen = true;
    if (this.documentElement.requestFullscreen) {
      return this.documentElement.requestFullscreen();
    } else if (this.documentElement.mozRequestFullScreen) {
      /* Firefox */
      return this.documentElement.mozRequestFullScreen();
    } else if (this.documentElement.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      return this.documentElement.webkitRequestFullscreen();
    } else if (this.documentElement.msRequestFullscreen) {
      /* IE/Edge */
      return this.documentElement.msRequestFullscreen();
    }
  }

  /* Close fullscreen */
  _closeFullscreenFunction() {
    if (this.document.exitFullscreen) {
      return this.document.exitFullscreen();
    } else if (this.document.mozCancelFullScreen) {
      /* Firefox */
      return this.document.mozCancelFullScreen();
    } else if (this.document.webkitExitFullscreen) {
      /* Chrome, Safari and Opera */
      return this.document.webkitExitFullscreen();
    } else if (this.document.msExitFullscreen) {
      /* IE/Edge */
      return this.document.msExitFullscreen();
    }
  }

  closeIframe() {
    console.log('Trigger closeIframeMessage event on parent');
    window.parent.postMessage('closeIframeMessage', '*');
  }

  touchModel3d() {
    this.menuMainElementVariantOpened = false;
    this.menuAccessoryElementOpened = false;
    this.menuAccessoryElementVariantOpened = false;
  }
}
